import React, { useEffect, useMemo, useState, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import PagesIcon from '@atlaskit/icon/core/pages';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { SpotlightTarget } from '@atlaskit/onboarding';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';

import {
	BOOLEAN_LITERAL_FALSE,
	IDENTIFIER_ACKNOWLEDGED,
	IDENTIFIER_ASSIGNEE,
	IDENTIFIER_IS_SEEN,
	IDENTIFIER_STATUS,
	LOGICAL_OPERATOR_AND,
	OPERATOR_EQUAL_TO,
	STRING_LITERAL_STATUS_CLOSED,
	STRING_LITERAL_STATUS_OPEN,
} from '@atlassian/eoc-query-editor/tokenizer/constants';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { NAVIGATION_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import { FIXED_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import {
	LEVEL_THREE,
	LEVEL_TWO,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import {
	ENTITY_ID,
	MENU_ID_OPERATIONS,
} from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { toMenuIdFromMenuIdList } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/menu-id/index.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useFormattedMessageAndIcon } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-formatted-message-and-icon/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useIsActiveRoute, useQueryParam } from '@atlassian/react-resource-router';
import { OpsgenieSavedSearchAri } from '@atlassian/ari/opsgenie/saved-search';
import { ALERT_LIST_SPOTLIGHT_TARGETS } from '@atlassian/eoc-alert-common/constants';
import { eocGlobalAlertListRoute } from '@jira/router-routes__eoc-global-alerts-routes/src/eocGlobalAlertListRoute.tsx';
import { eocTeamAlertDetailRoute } from '@jira/router-routes__eoc-team-alerts-routes/src/eocTeamAlertDetailRoute.tsx';
import { useAlertsPopoutStore } from '../../operations-expandable-menu-item/action-buttons/useAlertsPopoutStore.tsx';
import type {
	AlertsMenuItemContentNew$key,
	AlertsMenuItemContentNew$data,
} from './__generated__/AlertsMenuItemContentNew.graphql';
import { AlertsFlyoutMenuItem } from './flyout-menu/index.tsx';
import { SavedSearchActionButtons } from './action-button/index.tsx';
import { StarSection } from './star-section/StarSection.tsx';

const allSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.all-search',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'All',
	description: 'Predefined search query in the alert list navigation.',
});

const openSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.open-search',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Open',
	description: 'Predefined search query in the alert list navigation.',
});

const closedSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.closed-search',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Closed',
	description: 'Predefined search query in the alert list navigation.',
});

const unackedSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.unacked-search',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Unacknowledged',
	description: 'Predefined search query in the alert list navigation.',
});

const notSeenSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.not-seen-search',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Not seen',
	description: 'Predefined search query in the alert list navigation.',
});

const assignedToMeSearchMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.assigned-to-me-search',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Assigned to me',
	description: 'Predefined search query in the alert list navigation.',
});

const defaultFiltersMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.default-filters',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Default filters',
	description:
		'Expandable menu item label displayed in the operations sidebar. When clicked, shows pre-configured filter options for alerts.',
});

const alertsMessageDefinition = defineMessage({
	id: 'navigation-apps-sidebar-nav4-operations.alerts',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Alerts',
	description: 'Assets dropdown item to link customer to Operations Alerts page.',
});

export function AlertsMenuItemContentNew({
	queryRef,
}: {
	queryRef: AlertsMenuItemContentNew$key;
}): React.JSX.Element {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<AlertsMenuItemContentNew$key>(
		graphql`
			fragment AlertsMenuItemContentNew on Query {
				opsgenie @required(action: LOG) {
					savedSearches(cloudId: $cloudId) @required(action: LOG) @optIn(to: "opsgenie-beta") {
						starred @required(action: THROW) {
							id @required(action: THROW)
							name @required(action: THROW)
							alertSearchQuery @required(action: THROW)
						}
						sharedWithMe @required(action: THROW) {
							id @required(action: THROW)
							name @required(action: THROW)
							alertSearchQuery @required(action: THROW)
						}
						createdByMe @required(action: THROW) {
							id @required(action: THROW)
							name @required(action: THROW)
							alertSearchQuery @required(action: THROW)
						}
					}
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);

	const [savedSearchIdFromURL] = useQueryParam('filter');
	const [alertSearchQueryFromURL] = useQueryParam('query');
	const [selectedFilter, setSelectedFilter] = useState('');
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const accountId = useAccountId();
	const [viewType] = useQueryParam('view');
	const { icon: pagesIcon } = useFormattedMessageAndIcon(alertsMessageDefinition, PagesIcon);
	const isAlertsRoute = useIsActiveRoute(
		new Set([eocGlobalAlertListRoute.name, eocTeamAlertDetailRoute.name]),
	);

	const { setLastVisitedSearch } = useAlertsPopoutStore();

	const { sharedWithMe, createdByMe, starred } = data?.opsgenie?.savedSearches || {};

	const defaultFilters = useMemo(
		() => [
			{ id: 'all', name: formatMessage(allSearchMessage), alertSearchQuery: '' },
			{
				id: 'open',
				name: formatMessage(openSearchMessage),
				alertSearchQuery: `${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_OPEN}`,
			},
			{
				id: 'closed',
				name: formatMessage(closedSearchMessage),
				alertSearchQuery: `${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_CLOSED}`,
			},
			{
				id: 'unacked',
				name: formatMessage(unackedSearchMessage),
				alertSearchQuery: `${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_OPEN} ${LOGICAL_OPERATOR_AND} ${IDENTIFIER_ACKNOWLEDGED}${OPERATOR_EQUAL_TO} ${BOOLEAN_LITERAL_FALSE}`,
			},
			{
				id: 'notSeen',
				name: formatMessage(notSeenSearchMessage),
				alertSearchQuery: `${IDENTIFIER_IS_SEEN}${OPERATOR_EQUAL_TO} ${BOOLEAN_LITERAL_FALSE}`,
			},
			{
				id: 'assignedToMe',
				name: formatMessage(assignedToMeSearchMessage),
				alertSearchQuery: `${IDENTIFIER_ASSIGNEE}${OPERATOR_EQUAL_TO} "${accountId || 'me'}"`,
			},
		],
		[formatMessage, accountId],
	);
	const defaultFilterMenuIds = getParentMenuId(defaultFilters);

	const starredIds = useMemo(() => (starred ? starred.map((item) => item.id) : []), [starred]);

	const defaultFilterWithComponentList = getComponentAddedSearchObjects(
		defaultFilters,
		pagesIcon,
		viewType,
		'default',
		savedSearchIdFromURL,
		setSelectedFilter,
		createAnalyticsEvent,
		setLastVisitedSearch,
		isAlertsRoute,
		starredIds,
	);
	const sharedWithMeWithComponentList = getComponentAddedSearchObjects(
		sharedWithMe || [],
		pagesIcon,
		viewType,
		'sharedWithMe',
		savedSearchIdFromURL,
		setSelectedFilter,
		createAnalyticsEvent,
		setLastVisitedSearch,
		isAlertsRoute,
		starredIds,
	);
	const createdByMeWithComponentList = getComponentAddedSearchObjects(
		createdByMe || [],
		pagesIcon,
		viewType,
		'createdByMe',
		savedSearchIdFromURL,
		setSelectedFilter,
		createAnalyticsEvent,
		setLastVisitedSearch,
		isAlertsRoute,
		starredIds,
	);
	const allFiltersWithComponentList = useMemo(
		() => [
			...defaultFilterWithComponentList,
			...sharedWithMeWithComponentList,
			...createdByMeWithComponentList,
		],
		[createdByMeWithComponentList, defaultFilterWithComponentList, sharedWithMeWithComponentList],
	);

	const getSelectedFilterComponentIfNotDefault = useMemo(
		() => (filterId: string) => {
			if (
				defaultFilters.find((item) => item.id === filterId) ||
				starred?.find((item) => item.id === filterId)
			) {
				return;
			}

			const foundComponent = [
				...sharedWithMeWithComponentList,
				...createdByMeWithComponentList,
			].find((item) => item.id === filterId)?.component;

			return foundComponent;
		},
		[createdByMeWithComponentList, defaultFilters, sharedWithMeWithComponentList, starred],
	);

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		if (selectedFilter === '') {
			const customSearches = [...createdByMeWithComponentList, ...sharedWithMeWithComponentList];
			if (savedSearchIdFromURL) {
				const matchedItem = customSearches.find(
					(item) => OpsgenieSavedSearchAri.parse(item.id).savedSearchId === savedSearchIdFromURL,
				);
				if (matchedItem) {
					setSelectedFilter(matchedItem.id);
					return;
				}
			}
			if (alertSearchQueryFromURL) {
				const matchedItem = customSearches.find(
					(item) => item.alertSearchQuery === alertSearchQueryFromURL,
				);
				if (matchedItem) {
					setSelectedFilter(matchedItem.id);
				}
			}
		}
	}, [
		alertSearchQueryFromURL,
		allFiltersWithComponentList,
		createdByMeWithComponentList,
		savedSearchIdFromURL,
		selectedFilter,
		sharedWithMeWithComponentList,
	]);

	return (
		<UFOSegment name="nav4.sidebar.operations.alerts">
			<SpotlightTarget name={ALERT_LIST_SPOTLIGHT_TARGETS.sidebar}>
				<>
					<StarSection
						starred={starred ?? []}
						viewType={viewType}
						savedSearchIdFromURL={savedSearchIdFromURL}
						setSelectedFilter={setSelectedFilter}
						setLastVisitedSearch={setLastVisitedSearch}
					/>
					<Nav4ExpandableMenuItem
						menuId={defaultFilterMenuIds}
						onExpansionToggle={(hasExpanded) => {
							if (hasExpanded) {
								fireUIAnalytics(createAnalyticsEvent({}), {
									action: 'clicked',
									actionSubject: NAVIGATION_ITEM,
									actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
									attributes: {
										componentType: FIXED_ITEM,
										level: LEVEL_TWO,
										itemId: 'defaultFilters',
										parentItemId: 'alerts',
									},
								});
							}
						}}
					>
						<ExpandableMenuItemTrigger
							testId={getTestId(defaultFilterMenuIds)}
							interactionName="nav4-sidebar-operations-alerts"
						>
							{formatMessage(defaultFiltersMessage)}
						</ExpandableMenuItemTrigger>
						<ExpandableMenuItemContent>
							{defaultFilters.map((item) => {
								const href = `/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}`;
								return (
									<Nav4MenuLinkItem
										elemBefore={pagesIcon}
										key={item.id}
										href={href}
										menuId={ENTITY_ID.ALARM_QUEUE(item.alertSearchQuery)}
										onClick={() => {
											setSelectedFilter(item.id);
											setLastVisitedSearch(href);

											fireUIAnalytics(createAnalyticsEvent({}), {
												action: 'clicked',
												actionSubject: NAVIGATION_ITEM,
												actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
												attributes: {
													componentType: FIXED_ITEM,
													level: LEVEL_THREE,
													itemId: item.id,
													parentItemId: 'defaultFilters',
													itemType: 'defaultFilter',
												},
											});
										}}
									>
										{item.name}
									</Nav4MenuLinkItem>
								);
							})}
						</ExpandableMenuItemContent>
					</Nav4ExpandableMenuItem>

					<>{getSelectedFilterComponentIfNotDefault(selectedFilter)}</>

					<AlertsFlyoutMenuItem
						defaultFilterWithComponentList={defaultFilterWithComponentList}
						sharedWithMeWithComponentList={sharedWithMeWithComponentList}
						createdByMeWithComponentList={createdByMeWithComponentList}
						allFiltersWithComponentList={allFiltersWithComponentList}
					/>
				</>
			</SpotlightTarget>
		</UFOSegment>
	);
}

type AlertFilters = NonNullable<
	NonNullable<NonNullable<AlertsMenuItemContentNew$data>['opsgenie']>['savedSearches']
>['createdByMe'];

function getParentMenuId(arr?: AlertFilters) {
	return toMenuIdFromMenuIdList((arr || []).map((i) => ENTITY_ID.ALARM_QUEUE(i.alertSearchQuery)));
}

function getComponentAddedSearchObjects(
	arrayList: AlertFilters,
	pagesIcon: ReactElement,
	viewType: string | undefined,
	savedSearchType: 'default' | 'sharedWithMe' | 'createdByMe',
	savedSearchIdFromURL: string | undefined,
	setSelectedFilter: React.Dispatch<React.SetStateAction<string>>,
	createAnalyticsEvent: ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'],
	setLastVisitedSearch: (value: string) => void | Promise<void>,
	isAlertsRoute: boolean,
	starredIds: string[],
): Array<AlertFilters[number] & { component: ReactElement }> {
	return arrayList.map((item) => {
		const requiresSavedSearchId = item.id && savedSearchType !== 'default';
		const savedSearchId = requiresSavedSearchId
			? OpsgenieSavedSearchAri.parse(item.id).savedSearchId
			: '';
		const href = `/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}${requiresSavedSearchId ? `&filter=${savedSearchId}` : ''}`;

		return {
			...item,
			component: (
				<Nav4MenuLinkItem
					elemBefore={pagesIcon}
					key={item.id}
					href={href}
					menuId={ENTITY_ID.ALARM_QUEUE(
						savedSearchIdFromURL ? savedSearchId : item.alertSearchQuery,
					)}
					onClick={() => {
						setSelectedFilter(item.id);
						if (isAlertsRoute) {
							setLastVisitedSearch(href);
						}

						fireUIAnalytics(createAnalyticsEvent({}), {
							action: 'clicked',
							actionSubject: NAVIGATION_ITEM,
							actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
							attributes: {
								componentType: FIXED_ITEM,
								level: LEVEL_THREE,
								itemId: item.id,
								parentItemId: 'defaultFilters',
								itemType: 'defaultFilter',
							},
						});
					}}
					actionsOnHover={
						savedSearchType !== 'default' ? (
							<SavedSearchActionButtons
								savedSearchType={savedSearchType}
								savedSearch={item}
								isStarred={starredIds.includes(item.id)}
							/>
						) : undefined
					}
				>
					{item.name}
				</Nav4MenuLinkItem>
			),
		};
	});
}
